import { useState, useEffect, useCallback, useRef } from "react";

export type UseFetchResult<T> = {
  data: T | undefined;
  isLoading: boolean;
  error: string | null;
  /** Re-runs the loader. Replaces `data` on success, sets `error` on failure. */
  refetch: () => Promise<void>;
  /** Updates `data` directly — for optimistic updates after a mutation. */
  setData: React.Dispatch<React.SetStateAction<T | undefined>>;
};

export type UseFetchOptions = {
  /** When false, skips the fetch entirely (no loading, no error). Default: true. */
  enabled?: boolean;
  /** Transforms a thrown error into a user-facing message. Default: error.message. */
  transformError?: (err: unknown) => string;
};

/**
 * Loads data on mount and whenever `deps` change. Cancels stale results so a
 * late response from an earlier render can't overwrite a newer one — replaces
 * the standard useState + useEffect + try/catch + cancelled-flag boilerplate.
 *
 * @example
 * const { data: items = [], isLoading, error, refetch, setData } = useFetch(
 *   () => getItemsForId(id),
 *   [id],
 *   { enabled: !!id, transformError: getUserFriendlyErrorMessage }
 * );
 */
export function useFetch<T>(
  loader: () => Promise<T>,
  deps: React.DependencyList,
  options?: UseFetchOptions
): UseFetchResult<T> {
  const enabled = options?.enabled ?? true;

  const [data, setData] = useState<T | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  // Pin the loader and transformer so identity changes between renders don't
  // restart the fetch effect — only the explicit `deps` should trigger reloads.
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const transformErrorRef = useRef(options?.transformError);
  transformErrorRef.current = options?.transformError;

  const cancelTokenRef = useRef<{ cancelled: boolean } | null>(null);

  const runLoader = useCallback(async () => {
    if (cancelTokenRef.current) cancelTokenRef.current.cancelled = true;
    const token = { cancelled: false };
    cancelTokenRef.current = token;

    setIsLoading(true);
    setError(null);
    try {
      const result = await loaderRef.current();
      if (!token.cancelled) setData(result);
    } catch (err) {
      if (!token.cancelled) {
        const message = transformErrorRef.current
          ? transformErrorRef.current(err)
          : err instanceof Error
            ? err.message
            : "Unknown error";
        setError(message);
      }
    } finally {
      if (!token.cancelled) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return undefined;
    }
    runLoader();
    return () => {
      if (cancelTokenRef.current) cancelTokenRef.current.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  return { data, isLoading, error, refetch: runLoader, setData };
}
